import React, { useEffect } from 'react'
import Card from '../Card/Card'
import { ConnectionModel, ConnectionsApiModel, IbcConnectionMetadata } from 'src/constants/DataModels'
import API from 'src/api/api'
import { useAppSelector } from 'src/store/store'
import './Connections.css'
import Tabs from '../shared/Tabs'
import { BridgeId, getBridgeNameFromBridgeId } from 'src/constants/BlockchainConstants'
import { BlockchainWithIcon } from 'src/components/shared/BlockchainWithIcon'
import { ExplorerAddressLink } from 'src/components/shared/ExplorerAddressLink'
import { CopyText } from 'src/components/shared/CopyText'
import { maskHash } from 'src/constants/ConversionUtils'
import BlockchainIcon from 'src/assets/components/BlockchainIcon'

const ConnectionsPage: React.FC = () => {
  const [connections, setConnections] = React.useState<ConnectionModel[] | null>(null)
  const selector = useAppSelector
  const network = selector((state) => state.NetworkReducer.network)
  const [currentBridge, setCurrentBridge] = React.useState(BridgeId.Axelar)

  useEffect(() => {
    const requestConnections = async () => {
      const response = await API.get<ConnectionsApiModel>('/connections')
      if (response.status === 200) {
        const { data: connections } = response.data
        setConnections(connections)
      }
    }
    requestConnections()
  }, [network])

  type BridgeConnection = {
    [bridgeId: number]: ConnectionModel[]
  }

  const bridgeConnectionsMap: BridgeConnection = connections?.reduce((acc: BridgeConnection, connection: ConnectionModel) => {
    const bridgeId = connection.bridge_id
    if (!acc[bridgeId]) acc[bridgeId] = []
    acc[bridgeId].push(connection)
    return acc
  }, {}) || {}

  return (
    <div className="container main-wrapper">
      <Card title="Connections">
        <Tabs
          wrapperClass={'connection-tabs-header'}
          tabs={
            Object.keys(bridgeConnectionsMap).map((bridge_id) => {
              const bridgeId = Number(bridge_id)
              return {
                label: (
                  <div className={'bridge-title'}>
                    <span>{`[${bridgeId}] ${getBridgeNameFromBridgeId(Number(bridgeId))}`}</span>
                    <small>{`(${bridgeConnectionsMap[Number(bridgeId)].length} chains)`}</small>
                  </div>
                ),
                value: bridge_id,
                selected: currentBridge === bridgeId,
                onClick: () => setCurrentBridge(bridgeId),
              }
            })
          }
        />
        {
          Object.entries(bridgeConnectionsMap).map(([bridge_id, connections]) => {
            const bridgeId = Number(bridge_id)
            if (bridgeId !== currentBridge) return null
            return (
              <div className="all-connections" key={bridgeId}>
                {connections.map((connection: ConnectionModel) => (
                  <div className="connection-card" key={connection.connection_id}>
                    <div className="connection-header">
                      <div>
                        <BlockchainWithIcon blockchain={connection.blockchain} />
                        <CopyText
                          className={'connection-id-label'}
                          text={<div>ID: {maskHash(connection.connection_id, 20)}</div>}
                          copyText={connection.connection_id}
                        />
                      </div>
                      <div className="status-group">
                        <span className="connection-status-pill">{connection.encoding}</span>
                        <span
                          className={`connection-status-pill ${connection.is_enabled ? 'green' : 'gray'}`}
                        >{connection.is_enabled ? 'active' : 'inactive'}</span>
                      </div>
                    </div>
                    <div className="connection-body">
                      {displayFieldsForConnection(connection)}
                      <div className="field">
                        <label>Tokens Registered</label>
                        <span className="token-badge">{connection.external_token_count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          })
        }
      </Card>
    </div>
  )
}

const displayFieldsForConnection = (connection: ConnectionModel) => {
  switch (connection.bridge_id) {
    case BridgeId.IBC:
      return displayIbcFields(connection)
    case BridgeId.Axelar:
    case BridgeId.Polynetwork:
      return displayAxelarFields(connection)
    default:
      return null
  }
}

const displayAxelarFields = (connection: ConnectionModel) => {
  return <>
    <div className="field">
      <label>Chain ID</label>
      <span>{connection.chain_id}</span>
    </div>
    <div className="field">
      <label>Gateway</label>
      <ExplorerAddressLink blockchain={connection.blockchain}
                           address={connection.gateway_address}
                           shouldTruncate={true} truncateAmount={20}
      />
    </div>
    <div className="field">
      <label>Escrow</label>
      <ExplorerAddressLink blockchain={'carbon'}
                           address={connection.escrow_address}
                           shouldTruncate={true} truncateAmount={20}
      />
    </div>
    <div className="field">
      <label>Early Confirm Relays</label>
      <span>{connection.is_optimistic_confirm ? 'Yes' : 'No'}</span>
    </div>
  </>
}


const displayIbcFields = (connection: ConnectionModel) => {
  const metadata = connection.metadata as IbcConnectionMetadata
  const { counterparty_channel, counterparty_port } = metadata
  return <>
    <div className="field">
      <label><BlockchainIcon blockchain={'carbon'} className={'ibc-field-icon'}/> Source</label>
      <div className="ibc-grid">
        <span className="ibc-label">Port:</span>
        <span className="ibc-value">{connection.gateway_address}</span>
        <span className="ibc-label">Channel:</span>
        <span className="ibc-value">{connection.chain_id}</span>
      </div>
    </div>
    <div className="field">
      <label><BlockchainIcon blockchain={connection.blockchain} className={'ibc-field-icon'}/> Counterparty</label>
      <div className="ibc-grid">
        <span className="ibc-label">Port:</span>
        <span className="ibc-value truncate">{counterparty_port}</span>
        <span className="ibc-label">Channel:</span>
        <span className="ibc-value">{counterparty_channel}</span>
      </div>
    </div>
  </>
}


export default ConnectionsPage
